import { BehaviourField } from '@/configuration/BehaviourField';

export const behaviours = {
  [BehaviourField.Bhv_Generosity]: {
    title: 'Generosity',
    subtitle() {
      return 'of spirit to care deeply about each other and our clients.';
    },
    flavourText() {
      return 'Acknowledging and making room for others; giving everyone the benefit of the doubt; assuming positive intent; showing humility; being thankful; sharing, not hoarding; being transparent; modelling integrity; being authentic; doing the right thing.';
    },
    examples: [
      'Bring others in – share client relationships, don’t protect them as something you own',
      'Operate with highest integrity with clients, even if it impacts us commercially – practice essential rightness, don’t compromise away our values',
      'Be authentic, open and fair with each other with no pretence or guile – operate transparently, don’t game the system to protect your own resources or inflate your own dashboard',
    ],
  },
  [BehaviourField.Bhv_Passion]: {
    title: 'Passion',
    subtitle(isSelfSurvey) {
      const pronoun = isSelfSurvey ? 'your' : 'their';
      return `and presence to be ${pronoun} best self; being all in; bringing ${pronoun} best self.`;
    },
    flavourText(isSelfSurvey) {
      const pronoun = isSelfSurvey ? 'yourself' : 'themselves';
      return `Letting ${pronoun} be known on a personal level; being present mentally and emotionally.`;
    },
    examples: [
      'Be your best self by bringing a positive, optimistic and can-do spirit to our work and to each other – add energy, don’t diminish it',
      'Be present mentally and emotionally and give each other the ‘gift’ of attention – don’t do things distractedly with fragmented focus',
      'Recognize and take time to enjoy and celebrate good moments – call out strengths, share praise and joy with each other',
    ],
  },
  [BehaviourField.Bhv_Solidarity]: {
    title: 'Solidarity',
    subtitle() {
      return 'to be a winning team: collaborating deeply for team success.';
    },
    flavourText(isSelfSurvey) {
      const pronoun = isSelfSurvey ? 'your' : 'their';
      return `Acting as one inclusive team, one partnership with unities of interests and objectives; taking collective responsibility; putting the needs of the team or the partnership in front of ${pronoun} own.`;
    },
    examples: [
      'Don’t bring in the same 2-3 Partners into opportunities that you always work with and trust, rather than “risking” working with other Partners with potentially better expertise',
      'Help other partners to be successful, don’t just self-optimize - Intentionally include the 2nd person you think of, team broadly, involve widely and make all feel valued',
      'Use personal political & relational capital to support colleagues in the face of challenges and show we have each other’s backs e.g. defend each other in the face of unreasonable client staffing requests or speak up when clients use inappropriate behaviour with our teams',
    ],
  },
  [BehaviourField.Bhv_Curiosity]: {
    title: 'Curiosity',
    subtitle() {
      return 'and drive to explore new ground: being a lifelong learner.';
    },
    flavourText() {
      return 'Asking more than telling; seeking new and fresh experiences; capitalizing on our diversity and uniqueness as people; pushing for innovation; offering apprenticeship; expanding the mental agility of others.';
    },
    examples: [
      'Be a constant learner and always strive for more knowledge, not allowing our expertise to stagnate – expand your horizons, and plot your personal development - don’t default to a fixed mindset',
      'Step back from tasks and deliverables to have more probing client conversations e.g. “Are you winning in the market? – ask the big questions don’t just go with the flow; move from deck to dialogue',
      'Understand, value and utilize our differences as Partners to drive more innovative solutions and take new ground',
    ],
  },
  [BehaviourField.Bhv_Boldness]: {
    title: 'Boldness',
    subtitle() {
      return 'to be distinctive and break from the status quo.';
    },
    flavourText(isSelfSurvey) {
      const pronoun = isSelfSurvey ? 'your' : 'their';
      return `Taking personal risk and ownership; daring to fail; being ambitious for ${pronoun} teams and our firm; having a bias for action; having the courage of ${pronoun} convictions.`;
    },
    examples: [
      'Create project whitespace to reflect and discuss: Are we thinking far enough? - push boundaries that stretch our own thinking and that of our clients - don’t just deliver cut and paste',
      'Seek the best talents in every pursuit beyond your existing core network - Bring our A game/A team into the room, don’t just default to the group you are comfortable with',
      'Celebrating differences & diversity - Recognize when diversity is not being leveraged and address it, don’t leave it to someone else',
    ],
  },
  [BehaviourField.Bhv_FairTreatment]: {
    title: 'Fair Treatment',
    flavourText() {
      return 'Making sure that opportunities, resources and rewards are allocated to team members in an equitable way.';
    },
  },
  [BehaviourField.Bhv_IntegratingDifferences]: {
    title: 'Integrating Differences',
    flavourText() {
      return 'Emphasizing a variety of inputs and prioritizing a diversity of perspective to improve our work at Kearney.';
    },
    questionText(isSelfSurvey, params) {
      if (isSelfSurvey) {
        return 'How often have you seen yourself show you are <strong>Integrating Differences</strong>?';
      }

      return `How often have you seen <span class="primary--text">${params.EvaluateeFullName}</span> show they are <strong>Integrating Differences</strong>?`;
    },
  },
  [BehaviourField.Bhv_DecisionMaking]: {
    title: 'Decision Making',
    flavourText() {
      return 'Frequently considering the ideas and suggestions from a broad group of people and, when possible, collaborating to make decisions.';
    },
    questionText(isSelfSurvey, params) {
      if (isSelfSurvey) {
        return 'How often have you seen yourself demonstrate this kind of <strong>Decision Making</strong>?';
      }

      return `How often have you seen <span class="primary--text">${params.EvaluateeFullName}</span> demonstrate this kind of <strong>Decision Making</strong>?`;
    },
  },
  [BehaviourField.Bhv_PsychologicalSafety]: {
    title: 'Psychological Safety',
    flavourText() {
      return 'Helping team members to bring a sense of true self into the team and contribute equally without fear of negative consequences.';
    },
    questionText(isSelfSurvey, params) {
      if (isSelfSurvey) {
        return 'How often have you seen yourself build <strong>Psychological Safety</strong>?';
      }

      return `How often have you seen <span class="primary--text">${params.EvaluateeFullName}</span> build <strong>Psychological Safety</strong>?`;
    },
  },
  [BehaviourField.Bhv_Trust]: {
    title: 'Trust',
    flavourText() {
      return 'The extent to which the team members feel they can rely on the leader and the organization to act truthfully and in the best interest of the team.';
    },
    questionText(isSelfSurvey, params) {
      if (isSelfSurvey) {
        return 'How often have you seen yourself nurture <strong>Trust</strong>?';
      }

      return `How often have you seen <span class="primary--text">${params.EvaluateeFullName}</span> nurture <strong>Trust</strong>?`;
    },
  },
  [BehaviourField.Bhv_Belonging]: {
    title: 'Belonging',
    flavourText() {
      return 'Helping employees feel that they can contribute as a full member of the organization.';
    },
    questionText(isSelfSurvey, params) {
      if (isSelfSurvey) {
        return 'How often have you seen yourself build <strong>Belonging</strong>?';
      }

      return `How often have you seen <span class="primary--text">${params.EvaluateeFullName}</span> build <strong>Belonging</strong>?`;
    },
  },
};
